const toRadians = (degrees: number): number => {
  return degrees * (Math.PI / 180)
}

/**
 * Calculates the distance between two points on the map using their latitudes and longitudes using the Haversine formula
 * @param location1 object with the latitude and longitude in degrees of the first point
 * @param location2 object with the latitude and longitudein degrees of the second point
 * @returns distance in kilometers
 */
export const calculateDistanceWithHaversineInKilometers = ({
  location1,
  location2
}: {
  location1: google.maps.LatLngLiteral
  location2: google.maps.LatLngLiteral
}): number => {
  const { lat: lat1, lng: lon1 } = location1
  const { lat: lat2, lng: lon2 } = location2
  const R = 6371 // Radius of the Earth in kilometers.

  const dLat = toRadians(lat2 - lat1)
  const dLon = toRadians(lon2 - lon1)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  const distance = R * c

  return distance
}
/**
 * Calculate the radius of a circle that encloses a rectangular/square area on the map
 * using the diagonal of the rectangle as the diameter of the circle.
 *
 * @param north latitude of the northernmost point on the visible map.
 * @param south latitude of the southernmost point on the visible map.
 * @param east longitude of the easternmost point on the visible map.
 * @param west longitude of the westernmost point on the visible map.
 * @returns radius in meters
 */
export const calculateRadiusInMeters = (
  north: number,
  south: number,
  east: number,
  west: number
): number => {
  const latMid = (north + south) / 2
  const deltaLat = north - south
  const deltaLon = east - west

  const deltaLatKm = deltaLat * 111.32
  const deltaLonKm = deltaLon * 111.32 * Math.cos(toRadians(latMid))

  const diagonal = Math.sqrt(Math.pow(deltaLatKm, 2) + Math.pow(deltaLonKm, 2))
  const radius = diagonal / 2

  const radiusInMeters = Math.round(radius * 1000)

  return radiusInMeters
}
